<script setup lang="ts">
import { computed, h, inject, onMounted, ref, watch, type Component, type VNode } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NIcon, type DropdownOption } from 'naive-ui';
import {
  IconAdd,
  IconArchive,
  IconBarChart,
  IconBulb,
  IconBusiness,
  IconCash,
  IconChat,
  IconCheckCircle,
  IconCode,
  IconCopy,
  IconDocumentText,
  IconDocuments,
  IconEdit,
  IconFlash,
  IconFolder,
  IconGrid,
  IconHelp,
  IconHome,
  IconLogout,
  IconMail,
  IconNetwork,
  IconPalette,
  IconPeople,
  IconPieChart,
  IconPuzzle,
  IconReader,
  IconSettings,
  IconShield,
  IconSign,
  IconSwap,
  IconTime,
  IconUser,
} from '@/components/design/icons';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaDivider from '@/components/design/base/GaiaDivider.vue';
import GaiaDropdown from '@/components/design/base/GaiaDropdown.vue';
import GaiaNavItem from '@/components/design/domain/navigation/GaiaNavItem.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaProgressBar from '@/components/design/base/GaiaProgressBar.vue';
import GaiaSearchInput from '@/components/design/base/inputs/GaiaSearchInput.vue';
import GaiaSidebar from '@/components/design/domain/navigation/GaiaSidebar.vue';
import GaiaUserRow from '@/components/design/domain/navigation/GaiaUserRow.vue';
import GaiaWorkspaceSwitcher from '@/components/design/domain/navigation/GaiaWorkspaceSwitcher.vue';
import CreateOrganisationModal from '@/components/organisation/CreateOrganisationModal.vue';
import { gaiaFonts, gaiaNeutral, gaiaStatus } from '@/components/design/themes/tokens';
import type { GlobalSearchReturn } from '@/composables/useGlobalSearch';
import { useAuthStore } from '@/stores/auth';
import { useChangelogStore } from '@/stores/changelogStore';
import { useOrganizationStore } from '@/stores/organizationStore';
import { useTourStore } from '@/stores/tourStore';
import { logger } from '@/utils/logger';

// The app's sidebar composition. Still mid-rebuild: the GaiaSidebar shell is
// mounted with the account row in #footer, and the rest goes back in piece by
// piece (GaiaWorkspaceSwitcher for #header, GaiaSearchInput for #search,
// GaiaNavItem rows in the default slot). The full inventory of what used to
// live here is written down in sidebar.md at the repo root.

// DefaultLayout owns the state so route meta (`collapseSidebar`) can drive it.
const collapsed = defineModel<boolean>('collapsed', { default: false });

const authStore = useAuthStore();
const changelogStore = useChangelogStore();
const organizationStore = useOrganizationStore();
const tourStore = useTourStore();
const route = useRoute();
const router = useRouter();

function goTo(path: string): void {
  void router.push(path);
}

/**
 * A row owns a set of path prefixes rather than just its own path: the two
 * review detail pages live outside their list route's path (`/review-contract/…`
 * opens from the board, `/review-table/…` from the table), and the old sidebar's
 * parent-path matching missed both — opening a review lit up Home instead.
 */
function isActive(prefixes: readonly string[]): boolean {
  return prefixes.some(prefix => route.path === prefix || route.path.startsWith(`${prefix}/`));
}

const CHAT_PREFIXES = ['/chat'] as const;

// ── Review ──────────────────────────────────────────────────────────────────
//
// Reviews comes from the old Contract Review section, where Board and Table were
// two rows over two routes; they are one route with a `?view=` switch now, so
// this is one row. Playbooks and Review Projects move here out of Repository,
// which does not get them back. Each row keeps its own gate, so the group shows
// whenever any one of them does.

// The two detail routes live outside the list's path (`/review-contract/…` for
// a single file, `/review-table/…` for the grid), so the row owns them too.
const REVIEWS_PREFIXES = ['/reviews', '/review-contract', '/review-table'] as const;
const PLAYBOOKS_PREFIXES = ['/repository/playbooks'] as const;
// Deliberately not /external/review-projects — that is the external-access
// route and belongs to no nav row.
const REVIEW_PROJECTS_PREFIXES = ['/repository/review-projects'] as const;

const canSeeReviews = computed(() => authStore.canUseReviewArea);
// Playbook permissions are granted as "use" or "use + create", so "use" alone is
// the honest gate — and the only one the route itself accepts.
const canSeePlaybooks = computed(() => authStore.userPermissions.can_use_playbooks);
// Already folds in the contract_review feature on the backend.
const canSeeReviewProjects = computed(() => authStore.userPermissions.can_use_review_projects);

const showReviewGroup = computed(
  () => canSeeReviews.value || canSeePlaybooks.value || canSeeReviewProjects.value
);

const isReviewRoute = computed(
  () =>
    isActive(REVIEWS_PREFIXES) || isActive(PLAYBOOKS_PREFIXES) || isActive(REVIEW_PROJECTS_PREFIXES)
);

// Opens itself when you land inside the group, and never closes itself again:
// collapsing a section is the user's call. Local state on purpose — nothing in
// the app persists nav expansion, and the old sidebar's was naive's own.
const reviewOpen = ref(isReviewRoute.value);

watch(isReviewRoute, inside => {
  if (inside) reviewOpen.value = true;
});

// ── Contract Management ─────────────────────────────────────────────────────
//
// Exactly the can_use_contract_management feature: views and calendars. The
// whole group is gated on it — router and sidebar agree.

// The view list and the view detail page sit on sibling paths rather than
// nested ones (…-views vs …-view-detail), so the row owns both.
const VIEWS_PREFIXES = ['/contract-management-views', '/contract-management-view-detail'] as const;
const CALENDAR_PREFIXES = ['/contract-management/calendar'] as const;

const canSeeContractManagement = computed(
  () => authStore.userPermissions.can_use_contract_management
);

const isContractManagementRoute = computed(
  () => isActive(VIEWS_PREFIXES) || isActive(CALENDAR_PREFIXES)
);

const contractManagementOpen = ref(isContractManagementRoute.value);

watch(isContractManagementRoute, inside => {
  if (inside) contractManagementOpen.value = true;
});

// ── Drafts ──────────────────────────────────────────────────────────────────
//
// The documents a user is drafting from templates, finished ones included: the
// row opens the drafts list, whose "New document" button leads into the wizard.
// Its siblings — Templates and the snippet library — are org configuration and
// live in the account menu's Organisation submenu instead.
//
// The prefixes cover the list, an open draft (/drafts/:uid) and the wizard's
// /new and /new/:templateUid entry points.
const DRAFT_PREFIXES = ['/contract-templating/drafts', '/contract-templating/new'] as const;

const canSeeDraft = computed(() => authStore.userPermissions.can_use_contract_generation);

// ── Signing ─────────────────────────────────────────────────────────────────
//
// Every signing route — the request list, the wizard entries, edit and detail —
// sits under this prefix, so the row stays lit throughout a signing flow.
const SIGNING_PREFIXES = ['/signing'] as const;

const canSeeSigning = computed(() => authStore.userPermissions.can_use_signing);

// ── Sibling products ────────────────────────────────────────────────────────
//
// Equity management lives in the Contracts app, so the link follows the same
// staging/production host split the rest of the GAIA products use.
const EQUITY_URL = ['dev', 'staging'].includes(import.meta.env.VITE_ENV)
  ? 'https://app.gaia-law.dev'
  : 'https://next.gaia.law';

function openEquityManagement(): void {
  window.open(EQUITY_URL, '_blank', 'noopener');
}

// ── Repository ──────────────────────────────────────────────────────────────
//
// Reference material, below the divider. No gates: none of these routes carries
// requiredPermissions, and the old sidebar listed all three unconditionally.
// The Contract Vault sits above the group as a top-level row, and has to stay
// open too — the Office add-in is sent there as its home.
const FILE_VAULT_PREFIXES = ['/vault'] as const;
const PROMPTS_PREFIXES = ['/repository/prompts'] as const;
const PROMPT_LIBRARY_PREFIXES = ['/prompt-collections'] as const;
const CLAUSE_LIBRARY_PREFIXES = ['/clause-library'] as const;
// GAIA's own curated prompts — a different page from the org's Prompt Library
// (/prompt-collections), which is why both rows live here side by side.
const GAIA_PROMPTS_PREFIXES = ['/prompt-library'] as const;
// Contract master data. Entity detail pages resolve through the shared
// /contract-management/:entityType/:uid/detail route, which lands under each
// row's own prefix.
const MATTER_PREFIXES = ['/contract-management/matter'] as const;
const CLAUSE_PREFIXES = ['/contract-management/clause'] as const;
const CONTRACT_TYPE_PREFIXES = ['/repository/contract-types'] as const;
const OBLIGATION_PREFIXES = ['/contract-management/obligations'] as const;
const CONTRACTUAL_PARTY_PREFIXES = ['/contract-management/contractual-party'] as const;

const isRepositoryRoute = computed(
  () =>
    isActive(PROMPTS_PREFIXES) ||
    isActive(PROMPT_LIBRARY_PREFIXES) ||
    isActive(MATTER_PREFIXES) ||
    isActive(CLAUSE_LIBRARY_PREFIXES) ||
    isActive(CLAUSE_PREFIXES) ||
    isActive(CONTRACT_TYPE_PREFIXES) ||
    isActive(OBLIGATION_PREFIXES) ||
    isActive(GAIA_PROMPTS_PREFIXES)
);

const repositoryOpen = ref(isRepositoryRoute.value);

watch(isRepositoryRoute, inside => {
  if (inside) repositoryOpen.value = true;
});

// ── Group children as data ───────────────────────────────────────────────────
//
// Each group's rows are declared once here and consumed twice: the expanded
// sidebar renders them as nav rows (v-for in the template), and the collapsed
// 64px rail — which hides every nested row via GaiaSidebar's
// `.gaia-nav-item.child` rule — serves the same rows as a right-side dropdown
// on the group parent. One definition, so the two presentations can't drift.

interface SidebarNavChild {
  label: string;
  path: string;
  /** What lights the row up — see `isActive`. */
  prefixes: readonly string[];
  /** Row is offered only while the gate passes; no gate means always. */
  gate?: () => boolean;
}

const reviewChildren: SidebarNavChild[] = [
  {
    label: navLabels.playbooks,
    path: '/repository/playbooks',
    prefixes: PLAYBOOKS_PREFIXES,
    gate: () => canSeePlaybooks.value,
  },
  {
    label: navLabels.reviewProjects,
    path: '/repository/review-projects',
    prefixes: REVIEW_PROJECTS_PREFIXES,
    gate: () => canSeeReviewProjects.value,
  },
  {
    label: navLabels.reviews,
    path: '/reviews',
    prefixes: REVIEWS_PREFIXES,
    gate: () => canSeeReviews.value,
  },
];

const contractManagementChildren: SidebarNavChild[] = [
  { label: navLabels.views, path: '/contract-management-views', prefixes: VIEWS_PREFIXES },
  {
    label: navLabels.calendars,
    path: '/contract-management/calendar',
    prefixes: CALENDAR_PREFIXES,
  },
];

// Prompt Library (/prompt-collections) and GAIA Prompts (/prompt-library) have no row any
// more: Prompts replaces them. Their pages stay reachable by URL until they're removed.
const repositoryChildren: SidebarNavChild[] = [
  { label: navLabels.matters, path: '/contract-management/matter', prefixes: MATTER_PREFIXES },
  {
    label: navLabels.contractTypes,
    path: '/repository/contract-types',
    prefixes: CONTRACT_TYPE_PREFIXES,
  },
  { label: navLabels.prompts, path: '/repository/prompts', prefixes: PROMPTS_PREFIXES },
  { label: navLabels.clauseLibrary, path: '/clause-library', prefixes: CLAUSE_LIBRARY_PREFIXES },
  { label: navLabels.clauses, path: '/contract-management/clause', prefixes: CLAUSE_PREFIXES },
  {
    label: navLabels.obligations,
    path: '/contract-management/obligations',
    prefixes: OBLIGATION_PREFIXES,
  },
];

const visibleReviewChildren = computed(() =>
  reviewChildren.filter(child => child.gate?.() ?? true)
);

// The same rows as dropdown options for the collapsed rail's flyouts.
const toMenuOption = (child: SidebarNavChild): { label: string; key: string } => ({
  label: child.label,
  key: child.path,
});

const reviewMenuOptions = computed(() => visibleReviewChildren.value.map(toMenuOption));
const contractManagementMenuOptions = contractManagementChildren.map(toMenuOption);
const repositoryMenuOptions = repositoryChildren.map(toMenuOption);

// Keys are the destinations.
function onGroupMenuSelect(key: string): void {
  goTo(key);
}

// The spotlight modal and the ⌘K shortcut both live in App.vue; this row is
// only a way in. It is a readonly input rather than a real one — the query is
// typed in the modal, so letting it take text here would mean two search boxes
// to keep in step. Opening on click only (not on focus): the modal returns
// focus to its trigger when it closes, which would reopen it forever.
const globalSearch = inject<GlobalSearchReturn>('globalSearch');

const shortcutHintStyle = {
  fontFamily: gaiaFonts.mono,
  fontSize: '11px',
  color: gaiaNeutral.textMuted,
};

// Naive puts `cursor: text` on the inner <input>; the row is a button here.
const searchInputProps = { style: 'cursor: pointer' };

/** "Janina Möllmann" → "JM"; falls back to the email's first letter. */
const initials = computed(() => {
  const fromName = (authStore.backendUser?.full_name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

  return fromName || (authStore.sessionEmail?.charAt(0).toUpperCase() ?? '');
});

const displayName = computed(
  () =>
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    authStore.backendUser?.full_name ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must fall through to the next operand
    authStore.sessionEmail?.split('@')[0] ||
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    authStore.sessionEmail ||
    'User'
);

// ── Insights credits ────────────────────────────────────────────────────────
//
// Fetched once, on mount. Spending refreshes the figure at the source
// (vaultStore, reviewsStore, DocumentInsightTableView), so the only thing a
// timer bought was credits *other people* in the org spend while this tab sits
// open — not worth a request every 15 minutes for the life of the session.
// Those land on the next mount or org switch instead.
const creditsUsed = computed(() => organizationStore.organizationAllowanceUsage ?? 0);
const creditsTotal = computed(
  () => organizationStore.activeOrganization?.allowed_insights_credits ?? 0
);

const creditsPercentage = computed(() => {
  const total = creditsTotal.value;
  const used = creditsUsed.value;
  if (total <= 0 || used < 0) return 0;
  return Math.min(Math.round((used / total) * 100), 100);
});

const creditsValue = computed(
  () => `${creditsUsed.value.toLocaleString()} / ${creditsTotal.value.toLocaleString()}`
);

// The bar carries the "running out" signal the old sidebar coloured in; below
// 75% it stays the design's default ink.
const creditsColor = computed(() => {
  if (creditsPercentage.value >= 90) return gaiaStatus.alert;
  if (creditsPercentage.value >= 75) return gaiaStatus.risk;
  return undefined;
});

// Not guarded on `activeOrganization`: the endpoint derives the org from the
// session, so there is no uid to wait for, and the bootstrap's org request is
// usually still in flight here. The user profile, by contrast, is already
// loaded, so it can tell us there is no organisation to ask about.
onMounted(() => {
  if (authStore.hasActiveOrganisation) {
    void organizationStore.fetchOwnActiveOrganizationAllowanceUsage();
  }
});

const ORGANISATION_KEY_PREFIX = 'switch-org:';
const SWITCH_TO_ADMIN_KEY = 'switch-to-admin';
const CREATE_ORGANISATION_KEY = 'create-organisation';
const TOUR_KEY_PREFIX = 'tour:';
const CHANGELOG_KEY = 'changelog';
const SIGN_OUT_KEY = 'sign-out';

// n-dropdown wants a render function per option; naive sizes what it returns
// from the Dropdown theme (16px), so the icons need no sizing of their own.
function renderIcon(icon: Component): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

// The two submenus carry the old sidebar's Settings and Admin Area sections
// (see sidebar.md). Their parents are labels, not links — neither /settings/
// admin-settings nor an "organisation" landing page exists as a route.
const TEMPLATING_ITEMS: DropdownOption[] = [
  {
    label: navLabels.templates,
    key: '/contract-templating/templates',
    icon: renderIcon(IconDocumentText),
  },
  {
    label: navLabels.snippets,
    key: '/contract-templating/snippets',
    icon: renderIcon(IconCopy),
  },
];

const ORGANISATION_ITEMS: DropdownOption[] = [
  { label: navLabels.general, key: '/settings/organisation', icon: renderIcon(IconSettings) },
  { label: navLabels.intakeAgents, key: '/intake-agents', icon: renderIcon(IconMail) },
  // Members, invitations and roles are tabs of one People page.
  { label: navLabels.people, key: '/settings/users', icon: renderIcon(IconPeople) },
  { label: navLabels.agentTasks, key: '/settings/agent-tasks', icon: renderIcon(IconFlash) },
  { label: navLabels.memories, key: '/settings/memories', icon: renderIcon(IconBulb) },
  // The system vault: platform-managed Templates / Intake Agent folders.
  { label: navLabels.systemFiles, key: '/settings/system-files', icon: renderIcon(IconFolder) },
  {
    label: navLabels.integrations,
    key: '/settings/service-account-token',
    icon: renderIcon(IconPuzzle),
  },
];

const ADMIN_AREA_ITEMS: DropdownOption[] = [
  {
    label: navLabels.adminOrganizations,
    key: '/settings/admin-organizations',
    icon: renderIcon(IconBusiness),
  },
  { label: navLabels.adminUsers, key: '/settings/admin-users', icon: renderIcon(IconPeople) },
  {
    label: navLabels.adminIntakeAgentLogs,
    key: '/settings/admin-intake-agent-logs',
    icon: renderIcon(IconMail),
  },
  {
    label: navLabels.modelCosts,
    key: '/settings/admin-model-pricing',
    icon: renderIcon(IconCash),
  },
  { label: navLabels.statistics, key: '/admin/statistics', icon: renderIcon(IconBarChart) },
];

// The /dev routes are registered only under import.meta.env.DEV (see
// router/index.ts), so the submenu is gated on the same flag rather than on a
// permission — in a production build the paths do not resolve at all.
const DEVELOPER_ITEMS: DropdownOption[] = [
  { label: navLabels.designSystem, key: '/dev/design', icon: renderIcon(IconPalette) },
  { label: navLabels.contractStateIcons, key: '/dev/icons', icon: renderIcon(IconGrid) },
  { label: navLabels.websocketDebug, key: '/dev/websocket', icon: renderIcon(IconNetwork) },
];

// The user's organizations, active one first and the rest alphabetical, so the
// list does not reshuffle as the active organization changes.
const userOrganizations = computed(() => {
  const organizations = authStore.organisations;
  const activeUid = organizationStore.activeOrganization?.uid;

  return [...organizations].sort((left, right) => {
    if (left.uid === activeUid) return -1;
    if (right.uid === activeUid) return 1;
    return left.name.localeCompare(right.name);
  });
});

const organizationMenuOptions = computed<DropdownOption[]>(() => {
  const activeUid = organizationStore.activeOrganization?.uid;

  const options: DropdownOption[] = userOrganizations.value.map(organization => {
    const isActive = organization.uid === activeUid;

    return {
      label: isActive ? `${organization.name} (Current)` : organization.name,
      key: `${ORGANISATION_KEY_PREFIX}${organization.uid}`,
      icon: renderIcon(isActive ? IconCheckCircle : IconBusiness),
      disabled: isActive,
    };
  });

  // Platform admins working inside a customer's organization need a way back.
  const activeOrganization = organizationStore.activeOrganization;
  if (
    authStore.userPermissions.is_platform_admin &&
    activeOrganization &&
    !activeOrganization.is_admin
  ) {
    options.push(
      { type: 'divider', key: 'divider-admin-org' },
      {
        label: 'Switch to Admin Org',
        key: SWITCH_TO_ADMIN_KEY,
        icon: renderIcon(IconSwap),
      }
    );
  }

  // Self-service creation is a deployment setting the backend stamps onto the
  // user's permissions. Last, under its own rule: it makes a workspace rather
  // than picking one, and for an org-less user it may be the only row.
  if (authStore.userPermissions.can_create_organisation) {
    if (options.length) options.push({ type: 'divider', key: 'divider-create-org' });
    options.push({
      label: 'Create organisation',
      key: CREATE_ORGANISATION_KEY,
      icon: renderIcon(IconAdd),
    });
  }

  return options;
});

const showCreateOrganisation = ref(false);

// Without an organisation the app has nothing to offer beyond home and the
// account row: every feature is org-scoped, and the router sends an org-less
// user back to "/" anyway. Home is what says so and offers the way in.
const hasOrganisation = computed(() => authStore.hasActiveOrganisation);

// A switch re-scopes every request the app makes, so the cheapest correct thing
// is to start over: a full document load onto the home screen drops all cached
// store data along with the old organization. (The old sidebar reloaded in
// place; landing on home avoids reloading into a page the new organization may
// not have.)
async function switchOrganization(key: string): Promise<void> {
  const switched = key.startsWith(ORGANISATION_KEY_PREFIX)
    ? await organizationStore.switchCurrentUserOrganization(
        key.slice(ORGANISATION_KEY_PREFIX.length)
      )
    : await organizationStore.switchToAdminOrganization();

  if (!switched) {
    // The store already logged the cause and holds the message.
    logger.error('Organization switch failed:', organizationStore.error);
    return;
  }

  window.location.assign('/');
}

function onOrganizationSelect(key: string | number): void {
  if (key === CREATE_ORGANISATION_KEY) {
    showCreateOrganisation.value = true;
    return;
  }
  void switchOrganization(String(key));
}

// Gating mirrors the old sidebar, and is cosmetic either way: every route below
// carries requiresOrgAdmin / requiresAdmin, and the router guard turns an
// unauthorized visit into a redirect to "/" on its own.
const menuOptions = computed<DropdownOption[]>(() => {
  const options: DropdownOption[] = [];

  // Credits belong to the organisation, so without one there is no meter to
  // read — a "0 / 0" bar would only look like an empty allowance.
  if (hasOrganisation.value) {
    options.push(
      // A render option rather than a labelled row: the meter is a readout, not
      // something to pick. naive gives the render no padding of its own.
      {
        key: 'credits',
        type: 'render',
        render: () =>
          h('div', { style: 'padding: 8px 10px 10px; min-width: 208px;' }, [
            h(GaiaProgressBar, {
              label: 'Insights credits',
              value: creditsValue.value,
              percentage: creditsPercentage.value,
              color: creditsColor.value,
            }),
          ]),
      },
      { type: 'divider', key: 'divider-credits' }
    );
  }

  options.push(
    { label: navLabels.profile, key: '/users/me', icon: renderIcon(IconUser) },
    { type: 'divider', key: 'divider-sections' }
  );

  if (authStore.userPermissions.is_org_admin) {
    const organisationItems = [...ORGANISATION_ITEMS];

    // Templating is org configuration, but it is also feature-gated — without
    // the generation feature its two pages exist only to bounce you back to "/".
    // Sits second, next to Home Screen: both are what the org looks like.
    if (authStore.userPermissions.can_use_contract_generation) {
      organisationItems.splice(1, 0, {
        label: navLabels.templating,
        key: 'templating',
        icon: renderIcon(IconDocuments),
        children: TEMPLATING_ITEMS,
      });
    }

    options.push({
      label: navLabels.organisation,
      key: 'organisation',
      icon: renderIcon(IconBusiness),
      children: organisationItems,
    });
  }

  if (authStore.userPermissions.is_platform_admin) {
    options.push({
      label: navLabels.adminArea,
      key: 'admin-area',
      icon: renderIcon(IconShield),
      children: ADMIN_AREA_ITEMS,
    });
  }

  // Sits with the other sections rather than under Help: these are pages, not
  // guided demos. No permission gate — the routes only exist in a dev build.
  if (import.meta.env.DEV) {
    options.push({
      label: navLabels.developer,
      key: 'developer',
      icon: renderIcon(IconCode),
      children: DEVELOPER_ITEMS,
    });
  }

  // The guided demos used to hang off a help button in a topbar that was never
  // mounted (and has since been deleted) — the cog is their only way in now.
  const tourItems: DropdownOption[] = tourStore.availableTours.map(tour => ({
    label: tour.title,
    key: `${TOUR_KEY_PREFIX}${tour.key}`,
  }));

  options.push({ type: 'divider', key: 'divider-sign-out' });

  // The tours walk through org-scoped pages, so without an organisation every
  // one of them would dead-end on the redirect to home.
  if (hasOrganisation.value) {
    options.push({
      label: 'Help',
      key: 'help',
      icon: renderIcon(IconHelp),
      children: tourItems.length
        ? tourItems
        : [{ label: 'No tours available', key: 'no-tours', disabled: true }],
    });
  }

  options.push(
    { label: 'Changelog', key: CHANGELOG_KEY, icon: renderIcon(IconTime) },
    { label: 'Sign out', key: SIGN_OUT_KEY, icon: renderIcon(IconLogout) }
  );

  return options;
});

// A full document navigation rather than a router push: signing out has to drop
// every store as well as the session, so the next sign-in in this tab does not
// inherit the previous user's data. authStore.logout() itself neither navigates
// nor reloads.
async function signOut(): Promise<void> {
  try {
    await authStore.logout();
  } catch (error) {
    logger.error('Sign-out failed:', error);
  }
  window.location.assign('/login');
}

function onMenuSelect(key: string | number): void {
  const target = String(key);

  if (target === SIGN_OUT_KEY) {
    void signOut();
    return;
  }

  // The changelog modal lives in App.vue; this is its only way in.
  if (target === CHANGELOG_KEY) {
    changelogStore.open();
    return;
  }

  if (target.startsWith(TOUR_KEY_PREFIX)) {
    tourStore.startTour(target.slice(TOUR_KEY_PREFIX.length), { resume: true });
    return;
  }

  // Submenu parents carry no path, so only real routes get here.
  if (target.startsWith('/')) {
    void router.push(target);
  }
}
</script>

<template>
  <gaia-sidebar v-model:collapsed="collapsed" collapsible>
    <template #header>
      <!-- The whole row is the trigger here, unlike the account row's cog: the
           switcher's chevrons say the row itself opens the list. Always shown:
           for an org-less user it is the one place in the chrome that lists
           the organisations they can enter (or lets them create one). -->
      <gaia-dropdown
        :options="organizationMenuOptions"
        :disabled="!organizationMenuOptions.length"
        placement="bottom-start"
        @select="onOrganizationSelect"
      >
        <gaia-workspace-switcher
          data-tour="sidebar.workspace"
          :name="organizationStore.activeOrganization?.name ?? 'No organisation'"
        />
      </gaia-dropdown>
    </template>

    <!-- Without an organisation there is nothing to search, so the slot is
         left out entirely (GaiaSidebar only draws the block when it is set). -->
    <template v-if="hasOrganisation" #search>
      <!-- Not a search field but the palette's trigger: readonly, so clicking
           it opens the overlay that does the searching. `panel` fills the rail's
           slot; the collapsed rail hides it by its .n-input class. -->
      <gaia-search-input
        readonly
        variant="panel"
        placeholder="Go to…"
        data-tour="home.search"
        :input-props="searchInputProps"
        :style="{ cursor: 'pointer' }"
        @click="globalSearch?.open()"
      >
        <template #suffix><span :style="shortcutHintStyle">⌘K</span></template>
      </gaia-search-input>
    </template>

    <!-- Home links to plain "/": when a conversation is open (`/` carries
         ?thread_uid=) it is what steps back out of that chat. Active on the
         path alone, so an open conversation still reads as being home. -->
    <gaia-nav-item :active="route.path === '/'" to="/">
      <template #icon><n-icon :component="IconHome" /></template>
      Home
    </gaia-nav-item>

    <gaia-nav-item v-if="hasOrganisation" :active="isActive(CHAT_PREFIXES)" to="/chat">
      <template #icon><n-icon :component="IconChat" /></template>
      Chat
    </gaia-nav-item>

    <!-- Drafts is a leaf: no `expanded` prop at all, so GaiaNavItem renders no
         chevron (passing false would still draw a closed one). -->
    <gaia-nav-item
      v-if="canSeeDraft"
      data-tour="sidebar.draft"
      :active="isActive(DRAFT_PREFIXES)"
      to="/contract-templating/drafts"
    >
      <template #icon><n-icon :component="IconEdit" /></template>
      {{ navLabels.draft }}
    </gaia-nav-item>

    <!-- The group's rows are siblings of its parent, not children of it: the
         parent only carries the chevron, and the rows render behind v-if so a
         closed group leaves nothing in the DOM (as in the design showcase). -->
    <template v-if="showReviewGroup">
      <!-- Expanded: the click toggles the group. Collapsed: the dropdown
           (disabled otherwise) opens the same rows as a flyout, because the
           rail hides nested rows entirely. -->
      <gaia-dropdown
        :options="reviewMenuOptions"
        :disabled="!collapsed"
        placement="right-start"
        @select="onGroupMenuSelect"
      >
        <gaia-nav-item
          data-tour="sidebar.review"
          :expanded="reviewOpen"
          :active="collapsed && isReviewRoute"
          @click="!collapsed && (reviewOpen = !reviewOpen)"
        >
          <template #icon><n-icon :component="IconReader" /></template>
          {{ navLabels.review }}
        </gaia-nav-item>
      </gaia-dropdown>

      <template v-if="reviewOpen">
        <gaia-nav-item
          v-for="child in visibleReviewChildren"
          :key="child.path"
          :depth="1"
          :active="isActive(child.prefixes)"
          :to="child.path"
        >
          {{ child.label }}
        </gaia-nav-item>
      </template>
    </template>

    <gaia-nav-item
      v-if="canSeeSigning"
      data-tour="sidebar.signing"
      :active="isActive(SIGNING_PREFIXES)"
      to="/signing"
    >
      <template #icon><n-icon :component="IconSign" /></template>
      {{ navLabels.sign }}
    </gaia-nav-item>

    <!-- One feature flag, one group: views and calendars. -->
    <template v-if="hasOrganisation && canSeeContractManagement">
      <gaia-dropdown
        :options="contractManagementMenuOptions"
        :disabled="!collapsed"
        placement="right-start"
        @select="onGroupMenuSelect"
      >
        <gaia-nav-item
          data-tour="sidebar.contract-management"
          :expanded="contractManagementOpen"
          :active="collapsed && isContractManagementRoute"
          @click="!collapsed && (contractManagementOpen = !contractManagementOpen)"
        >
          <template #icon><n-icon :component="IconGrid" /></template>
          {{ navLabels.contractManagement }}
        </gaia-nav-item>
      </gaia-dropdown>

      <template v-if="contractManagementOpen">
        <gaia-nav-item
          v-for="child in contractManagementChildren"
          :key="child.path"
          :depth="1"
          :active="isActive(child.prefixes)"
          :to="child.path"
        >
          {{ child.label }}
        </gaia-nav-item>
      </template>
    </template>

    <!-- Splits the contract workflow above from the reference material below.
         `subtle` is the hairline variant — the default one is a 2px primary rule
         meant for page sections. The margin is trimmed at the usage site (as the
         chat settings panel does): naive's 24px would swamp the nav's 1px gaps. -->
    <gaia-divider v-if="hasOrganisation" variant="subtle" :style="{ margin: '10px 0' }" />

    <!-- The contracts and their files themselves, so it heads the lower
         block — a row of its own rather than one of the Repository pages. -->
    <gaia-nav-item
      v-if="hasOrganisation"
      data-tour="sidebar.vault"
      :active="isActive(FILE_VAULT_PREFIXES)"
      to="/vault"
    >
      <template #icon><n-icon :component="IconArchive" /></template>
      {{ navLabels.fileVault }}
    </gaia-nav-item>

    <!-- Reference data — a row of its own, one click away rather than nested
         under Repository. -->
    <gaia-nav-item
      v-if="hasOrganisation"
      :active="isActive(CONTRACTUAL_PARTY_PREFIXES)"
      to="/contract-management/contractual-party"
    >
      <template #icon><n-icon :component="IconPeople" /></template>
      {{ navLabels.contractualParties }}
    </gaia-nav-item>

    <gaia-dropdown
      v-if="hasOrganisation"
      :options="repositoryMenuOptions"
      :disabled="!collapsed"
      placement="right-start"
      @select="onGroupMenuSelect"
    >
      <gaia-nav-item
        data-tour="sidebar.repository"
        :expanded="repositoryOpen"
        :active="collapsed && isRepositoryRoute"
        @click="!collapsed && (repositoryOpen = !repositoryOpen)"
      >
        <template #icon><n-icon :component="IconFolder" /></template>
        {{ navLabels.repository }}
      </gaia-nav-item>
    </gaia-dropdown>

    <template v-if="hasOrganisation && repositoryOpen">
      <gaia-nav-item
        v-for="child in repositoryChildren"
        :key="child.path"
        :depth="1"
        :active="isActive(child.prefixes)"
        :to="child.path"
      >
        {{ child.label }}
      </gaia-nav-item>
    </template>

    <template #footer>
      <!-- Leaves the app, so it says so: `count` is GaiaNavItem's right-aligned
           trailing slot, carrying an out-arrow here rather than a badge. It is
           hidden with the label in the collapsed rail, which is what we want. -->
      <gaia-nav-item v-if="hasOrganisation" count="↗" @click="openEquityManagement()">
        <template #icon><n-icon :component="IconPieChart" /></template>
        Equity Management
      </gaia-nav-item>

      <!-- Identity only, no sub-line: the organization gets its own home in
           the sidebar's #header (GaiaWorkspaceSwitcher). -->
      <gaia-user-row
        data-tour="sidebar.account"
        :name="displayName"
        :initials="initials"
        :menu-options="menuOptions"
        @menu-select="onMenuSelect"
      >
        <template #avatar>
          <gaia-avatar :src="authStore.sessionUser?.photoURL ?? undefined">
            {{ initials }}
          </gaia-avatar>
        </template>
        <template #trailing>
          <n-icon :component="IconSettings" :size="15" />
        </template>
      </gaia-user-row>
    </template>
  </gaia-sidebar>

  <create-organisation-modal v-model:show="showCreateOrganisation" />
</template>
